import { AccountRole } from 'src/types/userAuth';

import { isInternalPath } from './signInReturnTo';

export interface PostRegisterInput {
  role: AccountRole;
  /** The listing the anonymous user was gated from, if any (§5: return here after register). From the address bar: untrusted. */
  from?: string;
  /** Whether the personal user is on mobile (drives the Telegram mini-app deep link). */
  isMobile: boolean;
  /** Sales cabinet URL (external app) — where a dealer lands; empty means "not configured". */
  dealerCabinetUrl: string;
  /** Optional Telegram mini-app deep link; empty means "not configured". */
  telegramMiniAppUrl: string;
}

/** Navigate within the SPA (react-router) or leave to an external URL (window.location). */
export type PostRegisterDestination =
  | { kind: 'internal'; path: string }
  | { kind: 'external'; url: string };

/**
 * Where to send a user after they confirm registration (kickoff decision #1 + §5 gate). The
 * screen goes there by itself the moment the code is accepted — there is no last step to press
 * on any more (Tim's decision of 2026-10-31, app#368) — so a wrong answer here is where the
 * person ends up.
 *
 * - Dealer → the sales cabinet (external app), regardless of `from`; the web feed when no cabinet
 *   address is configured — an empty address reloads the page, and with it the empty sign-up form.
 * - Personal with a `from` (came through the listing gate) → back to that listing. Only an in-app
 *   path is followed: `from` is the address bar's, and `//elsewhere` would be another site.
 * - Personal on mobile with a configured mini-app link → the Telegram deep link.
 * - Personal otherwise (desktop, or mobile with no link) → the web feed.
 */
export const resolvePostRegisterDestination = (
  input: PostRegisterInput
): PostRegisterDestination => {
  const { role, from, isMobile, dealerCabinetUrl, telegramMiniAppUrl } = input;

  if (role === AccountRole.DEALER) {
    return dealerCabinetUrl ? { kind: 'external', url: dealerCabinetUrl } : { kind: 'internal', path: '/' };
  }

  if (from && isInternalPath(from)) {
    return { kind: 'internal', path: from };
  }

  if (isMobile && telegramMiniAppUrl) {
    return { kind: 'external', url: telegramMiniAppUrl };
  }

  return { kind: 'internal', path: '/' };
};
